// supervisor-attempt-verify-input.ts
//
// Split out of supervisor-attempt-cycle.ts (phase 04, D12 — trần dòng): pure
// move of the "load approved recipes then build verifyAttemptResult's input"
// block, so runAttemptCycle's file stays under the 200-line budget. Since
// SOL-04 it also names the ticket's recipe the verifier must hold evidence to.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import type { AttemptBaselineInventory } from "./attempt-baseline-inventory.js";
import { EVIDENCE_REQUIRED_ADAPTER_IDS } from "./command-adapter-table.js";
import { resolveRecipeCwdAgainstRepoRoot } from "./recipe-cwd-resolved-against-repo-root.js";
import type { VerifyAttemptResultInput } from "./result-verifier-types.js";
import { REVIEW_REPORT_REQUIRED_ADAPTER_IDS } from "./review-report-promotion-wiring.js";
import { VerificationProfileSchema } from "./schemas/verification-schema.js";
import type { SessionRunResult } from "./session-runner.js";
import type { AttemptCycleInput } from "./supervisor-attempt-cycle.js";
import { runVerificationRecipe, type VerificationRecipe } from "./verification-recipe-runner.js";

async function loadApprovedRecipes(repoRoot: string): Promise<VerificationRecipe[]> {
  const path = join(repoRoot, ".avatar", "verification-profile.json");
  const raw = await fs.readFile(path, "utf8");
  const profile = VerificationProfileSchema.parse(JSON.parse(raw));
  // `cwd` in the on-disk profile is authored relative to the execution repo
  // (portable across missions/tmp checkouts); resolve it here so the recipe
  // runner always receives an absolute cwd regardless of the caller's own
  // process.cwd().
  return profile.recipes.map((recipe) => resolveRecipeCwdAgainstRepoRoot(recipe, repoRoot));
}

export interface BuildVerifyAttemptInputArgs {
  input: AttemptCycleInput;
  sessionResult: SessionRunResult;
  limits: { deadlineMs: number };
  inputHash: string;
  runId: string;
  stagingDir: string;
  baseline: AttemptBaselineInventory;
}

/**
 * Loads the mission's approved verification recipes then assembles
 * `verifyAttemptResult`'s full input — pure move from `runAttemptCycle`'s
 * tail (only the surrounding function signature changed, every field maps
 * 1:1 to the original inline call, same comment provenance as before).
 */
export async function buildVerifyAttemptInput(
  args: BuildVerifyAttemptInputArgs,
): Promise<VerifyAttemptResultInput> {
  const { input, sessionResult, limits, inputHash, runId, stagingDir, baseline } = args;
  const approvedRecipes = await loadApprovedRecipes(input.repoRoot);
  const secretValues: string[] = [];
  const result = (sessionResult.result as Record<string, unknown>) ?? {};
  const runRecipe = (recipe: VerificationRecipe) =>
    runVerificationRecipe({ recipe, env: process.env, timeoutMs: limits.deadlineMs, secretValues });

  return {
    repoDir: input.repoRoot,
    controlRoot: input.missionWorkspace,
    attemptId: `${input.ticket.id}-${input.attemptNo}`,
    groupReaped: sessionResult.groupReaped ?? false,
    bindingPath: sessionResult.bindingPath ?? "",
    bindingHash: sessionResult.bindingHash ?? "",
    baselineInventory: baseline,
    expectedRunId: runId,
    expectedTicketRevision: input.ticket.revision,
    expectedInputHash: inputHash,
    stagingDir,
    approvedRecipes,
    // SOL-04: the controller, not the worker's evidence, picks the recipe that
    // must pass. Only adapters that owe evidence run a recipe at all; the rest
    // carry placeholder ids like "plan-bootstrap" that no profile defines.
    expectedRecipeId: EVIDENCE_REQUIRED_ADAPTER_IDS.includes(input.ticket.command_adapter)
      ? input.ticket.verification_recipe
      : undefined,
    allowedPaths: input.ticket.allowed_paths,
    result,
    isTestcaseAttempt: input.ticket.kind === "testcase",
    // P21 (layer A): only the review-local adapter owes a `review_report`.
    // Keyed on the ADAPTER, not the ticket `kind` — see supervisor-attempt-
    // cycle.ts's original comment (unchanged reasoning, moved verbatim).
    isReviewLocalAttempt: REVIEW_REPORT_REQUIRED_ADAPTER_IDS.includes(input.ticket.command_adapter),
    commandAdapter: input.ticket.command_adapter,
    ticketAcceptance: input.ticket.acceptance,
    runRecipe,
  };
}
